/*!
 * The body loft of the pinball SigmaBot. A loft turns the 2D outline of a body into a solid.
 * Each horizontal slice of the solid has the width of the outline at that height.
 *
 * Ported from sigmashake-vcs web/bots/three/loft.ts.
 * Upstream project: ClaudeAnimationBase - https://github.com/JohnHeibel/ClaudeAnimationBase
 * MIT License. Copyright (c) 2026 John Heibel. The full MIT license text is in NOTICE.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */
// SPDX-License-Identifier: GPL-3.0-or-later
// Changes from upstream: the clothes shell (`shellFromBody`) and its types are not ported. The
// table draws no clothes. The slices sit at the heights of the outline points (not on a fixed grid
// of 48), and a slice has 16 points (not 32). This cuts the triangles of a body by 90 percent.
// The loft math is the same. Pure functions: no clock, no random, no
// import of "three". Output is plain number arrays. Up is positive y here (y3 = -y2).

import { BODIES } from "./bodies.ts";
import { clamp, type Point, spanAt } from "./kit.ts";
import type { SigmaBotBody } from "./spec.ts";

/**
 * Points around one slice. The count is a multiple of 8, so a slice has points at x = +-half and
 * at z = 0, at the front and the back, and at the four corners of a boxy slice (45 degrees).
 * A bot is about 90 table units wide, so 16 points keep the sides of a round slice under 1 px.
 */
export const LOFT_SEGMENTS = 16;

/** Half depth of a slice = `DEPTH_K` x half width of the slice. */
export const DEPTH_K: Readonly<Record<SigmaBotBody, number>> = {
  blob: 0.78,
  bean: 0.78,
  box: 0.62,
  tall: 0.62,
  tiny: 0.78,
  round: 0.78,
  pear: 0.78,
  bunny: 0.78,
  cat: 0.78,
  robot: 0.62,
};

/** Exponent of the slice curve. 2 is an ellipse. 4 is a superellipse with flatter sides. */
export const SUPER_EXP: Readonly<Record<SigmaBotBody, number>> = {
  blob: 2,
  bean: 2,
  box: 4,
  tall: 2,
  tiny: 2,
  round: 2,
  pear: 2,
  bunny: 2,
  cat: 2,
  robot: 4,
};

const EPS = 1e-6;

/** One slice of the loft. */
export interface LoftRing {
  /** Height of the slice. Up is positive. */
  y: number;
  /** Center x of the slice. */
  cx: number;
  /** Half width of the slice. */
  half: number;
  /** Half depth of the slice. */
  depth: number;
}

/** Plain triangle mesh. `indices` hold triangles, counter-clockwise seen from outside. */
export interface MeshData {
  positions: number[];
  normals: number[];
  indices: number[];
}

/** An unknown body name falls back to `blob`, the same rule as the upstream renderer. */
export function bodyKind(name: string): SigmaBotBody {
  return Object.hasOwn(BODIES, name) ? (name as SigmaBotBody) : "blob";
}

/** The lowest and highest y of the 2D outline polygon (2D axis: up is negative). */
function extent(pts: readonly Point[]): [number, number] {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const p of pts) {
    lo = Math.min(lo, p.y);
    hi = Math.max(hi, p.y);
  }
  return [lo, hi];
}

/** The center and half width of the 2D outline at height `y` (up is positive), or null outside. */
function sliceAt(kind: SigmaBotBody, y: number): { cx: number; half: number } | null {
  const pts = BODIES[kind].pts;
  const [top, bot] = extent(pts);
  const y2 = -y;
  if (y2 < top - EPS || y2 > bot + EPS) return null;
  const [lo, hi] = spanAt(pts, clamp(y2, top + EPS, bot - EPS));
  return { cx: (lo + hi) / 2, half: (hi - lo) / 2 };
}

/** Heights closer than this are one height. */
const LEVEL_TOL = 1e-6;

/**
 * The heights of the slices (up is positive), from the foot to the top: each height where the 2D
 * outline has a point. The outline is a straight line between two such heights, so a slice at
 * each of them follows the outline exactly. The old fixed grid of 48 slices cut the corners of
 * the outline by up to 1 body unit and made 3000 triangles for each body. This needs about 10
 * slices.
 */
function sliceHeights(kind: SigmaBotBody): number[] {
  const ys = BODIES[kind].pts.map((p) => -p.y).sort((a, b) => a - b);
  const out: number[] = [];
  for (const y of ys) {
    const last = out[out.length - 1];
    if (last === undefined || y - last > LEVEL_TOL) out.push(y);
  }
  return out;
}

/** The slices of the loft, from the foot to the top (one slice at each outline height). */
export function loftProfile(body: string): LoftRing[] {
  const kind = bodyKind(body);
  return sliceHeights(kind).map((y) => {
    const slice = sliceAt(kind, y) ?? { cx: 0, half: 0 };
    return { y, cx: slice.cx, half: slice.half, depth: DEPTH_K[kind] * slice.half };
  });
}

/**
 * The z of the front surface of the loft at height `y` and sideways position `x`.
 * It is 0 at the outline edge (`|x - cx| = half`) and outside the body.
 */
export function frontZ(body: string, y: number, x = 0): number {
  const kind = bodyKind(body);
  const slice = sliceAt(kind, y);
  if (!slice || slice.half <= 0) return 0;
  const n = SUPER_EXP[kind];
  const q = Math.min(1, Math.abs(x - slice.cx) / slice.half);
  return DEPTH_K[kind] * slice.half * (1 - q ** n) ** (1 / n);
}

/** The half width of the body at height `y` (up is positive). It is 0 outside the body. */
export function halfAt(body: string, y: number): number {
  return sliceAt(bodyKind(body), y)?.half ?? 0;
}

/** Point `j` of a slice. Angle 0 is the right edge. Angle 90 degrees is the front. */
function ringPoint(ring: LoftRing, j: number, n: number): [number, number, number] {
  const t = (j / LOFT_SEGMENTS) * Math.PI * 2;
  // Snap the float noise of cos and sin at the quarter turns. Then z is exactly 0 at the edges.
  const c = Math.abs(Math.cos(t)) < 1e-12 ? 0 : Math.cos(t);
  const s = Math.abs(Math.sin(t)) < 1e-12 ? 0 : Math.sin(t);
  const e = 2 / n;
  return [
    ring.cx + ring.half * Math.sign(c) * Math.abs(c) ** e,
    ring.y,
    ring.depth * Math.sign(s) * Math.abs(s) ** e,
  ];
}

/** Smooth vertex normals. Each face adds its area-weighted normal to its three vertices. */
function smoothNormals(positions: ArrayLike<number>, indices: ArrayLike<number>): number[] {
  const out = new Array<number>(positions.length).fill(0);
  for (let f = 0; f + 2 < indices.length; f += 3) {
    const ia = (indices[f] as number) * 3;
    const ib = (indices[f + 1] as number) * 3;
    const ic = (indices[f + 2] as number) * 3;
    const ux = (positions[ib] as number) - (positions[ia] as number);
    const uy = (positions[ib + 1] as number) - (positions[ia + 1] as number);
    const uz = (positions[ib + 2] as number) - (positions[ia + 2] as number);
    const vx = (positions[ic] as number) - (positions[ia] as number);
    const vy = (positions[ic + 1] as number) - (positions[ia + 1] as number);
    const vz = (positions[ic + 2] as number) - (positions[ia + 2] as number);
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    for (const i of [ia, ib, ic]) {
      out[i] = (out[i] as number) + nx;
      out[i + 1] = (out[i + 1] as number) + ny;
      out[i + 2] = (out[i + 2] as number) + nz;
    }
  }
  for (let i = 0; i + 2 < out.length; i += 3) {
    const len = Math.hypot(out[i] as number, out[i + 1] as number, out[i + 2] as number) || 1;
    out[i] = (out[i] as number) / len;
    out[i + 1] = (out[i + 1] as number) / len;
    out[i + 2] = (out[i + 2] as number) / len;
  }
  return out;
}

/** The triangles of the side walls, the foot cap and the top cap. */
function loftIndices(slices: number, footCentre: number, topCentre: number): number[] {
  const indices: number[] = [];
  for (let i = 0; i < slices; i++) {
    for (let j = 0; j < LOFT_SEGMENTS; j++) {
      const j1 = (j + 1) % LOFT_SEGMENTS;
      const a = i * LOFT_SEGMENTS + j;
      const b = i * LOFT_SEGMENTS + j1;
      const c = (i + 1) * LOFT_SEGMENTS + j;
      const d = (i + 1) * LOFT_SEGMENTS + j1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const topRow = slices * LOFT_SEGMENTS;
  for (let j = 0; j < LOFT_SEGMENTS; j++) {
    const j1 = (j + 1) % LOFT_SEGMENTS;
    indices.push(footCentre, j, j1);
    indices.push(topCentre, topRow + j1, topRow + j);
  }
  return indices;
}

/**
 * The full loft mesh of one body. Vertex `ring * LOFT_SEGMENTS + j` is point `j` of slice `ring`.
 * The last two vertices are the centers of the foot cap and the top cap.
 */
export function loftBody(body: string): MeshData {
  const kind = bodyKind(body);
  const n = SUPER_EXP[kind];
  const rings = loftProfile(body);
  const positions: number[] = [];
  for (const ring of rings) {
    for (let j = 0; j < LOFT_SEGMENTS; j++) positions.push(...ringPoint(ring, j, n));
  }
  const first = rings[0] as LoftRing;
  const last = rings[rings.length - 1] as LoftRing;
  const footCentre = positions.length / 3;
  positions.push(first.cx, first.y, 0);
  const topCentre = positions.length / 3;
  positions.push(last.cx, last.y, 0);
  const indices = loftIndices(rings.length - 1, footCentre, topCentre);
  return { positions, normals: smoothNormals(positions, indices), indices };
}
